import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, SearchIcon, FilterIcon, ClockIcon, CheckIcon, CheckCircleIcon, AlertCircleIcon, XCircleIcon, LayoutGridIcon, LinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import ComparisonCard from './ComparisonCard';
import ReviewModal from './ReviewModal';
import { mockProductData, type Product } from '../utils/mockData';
import FloatingActionBar from './FloatingActionBar';
import BulkActionBar from './BulkActionBar';
const CategoryTab = ({
  title,
  icon,
  isActive,
  pendingCount = 0,
  onClick
}) => <motion.button onClick={onClick} className={`relative flex items-center px-4 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'}`} whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} data-id="element-187">
    {icon}
    <span className="ml-2" data-id="element-188">{title}</span>
    {pendingCount > 0 && <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full" data-id="element-189">
        {pendingCount}
      </span>}
  </motion.button>;
const ProductList = ({
  products,
  category,
  title,
  onSkipProduct
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [linkedProducts, setLinkedProducts] = useState<Product[]>([]);
  const [skippedProducts, setSkippedProducts] = useState<Product[]>([]);
  const [showReview, setShowReview] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeCategory, setActiveCategory] = useState(category);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const getRemainingCounts = () => {
    const allProducts = mockProductData;
    return {
      potentialMatch: allProducts.filter(p => p.matchCategory === 'potential-match' && !p.skipped).length,
      noMatch: allProducts.filter(p => p.matchCategory === 'no-match' && !p.skipped).length
    };
  };
  const categories = [{
    id: 'all',
    title: 'All Matches',
    icon: <LayoutGridIcon className="h-4 w-4 text-blue-500" data-id="element-190" />,
    pendingCount: linkedProducts.length + skippedProducts.length
  }, {
    id: 'perfect-match',
    title: 'Perfect Matches',
    icon: <CheckCircleIcon className="h-4 w-4 text-green-500" data-id="element-191" />,
    pendingCount: linkedProducts.filter(p => p.matchCategory === 'perfect-match').length
  }, {
    id: 'potential-match',
    title: 'Potential Matches',
    icon: <AlertCircleIcon className="h-4 w-4 text-yellow-500" data-id="element-192" />,
    pendingCount: linkedProducts.filter(p => p.matchCategory === 'potential-match').length
  }, {
    id: 'no-match',
    title: 'No Matches',
    icon: <XCircleIcon className="h-4 w-4 text-red-500" data-id="element-193" />,
    pendingCount: skippedProducts.filter(p => p.matchCategory === 'no-match').length
  }];
  const handleCategoryChange = newCategory => {
    setActiveCategory(newCategory);
  };
  const filteredProducts = products.filter(product => (product.title.toLowerCase().includes(searchTerm.toLowerCase()) || product.sku.toLowerCase().includes(searchTerm.toLowerCase())) && (activeCategory === 'all' || product.matchCategory === activeCategory));
  const handleBulkAction = (action: 'review' | 'unskip') => {
    console.log(`Bulk ${action} for:`, selectedProducts);
  };
  const handleProductLink = (product: Product) => {
    setLinkedProducts(prev => [...prev, product]);
  };
  const handleProductSkip = (product: Product) => {
    setSkippedProducts(prev => [...prev, product]);
  };
  const handleConfirmLinks = async () => {
    setIsLinking(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      skippedProducts.forEach(product => {
        onSkipProduct(product.id);
      });
      setShowSuccess(true);
    } finally {
      setIsLinking(false);
    }
  };
  const handleClose = () => {
    if (!isLinking) {
      setShowReview(false);
      setShowSuccess(false);
      setLinkedProducts([]);
      setSkippedProducts([]);
    }
  };
  const handleToggleSelect = (productId: string) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };
  const handleClearSelection = () => {
    setSelectedProducts(new Set());
    setLinkedProducts([]);
    setSkippedProducts([]);
  };
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'b' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowBulkActions(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  const hasChanges = linkedProducts.length > 0 || skippedProducts.length > 0;
  const totalChanges = linkedProducts.length + skippedProducts.length;
  return <div className="max-w-7xl mx-auto px-4 py-6 pb-24" data-id="element-194">
      <div className="flex items-center mb-6" data-id="element-195">
        <Link to="/" className="mr-4 p-2 hover:bg-gray-100 rounded-full" data-id="element-196">
          <ArrowLeftIcon className="h-5 w-5 text-gray-600" data-id="element-197" />
        </Link>
        <h1 className="text-xl font-bold text-gray-800" data-id="element-198">{title}</h1>
      </div>
      <div className="mb-6 bg-gray-50 p-1 rounded-lg flex space-x-2" data-id="element-199">
        {categories.map(cat => <CategoryTab key={cat.id} title={cat.title} icon={cat.icon} isActive={activeCategory === cat.id} pendingCount={cat.pendingCount} onClick={() => handleCategoryChange(cat.id)} data-id="element-200" />)}
      </div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-201">
        <div className="flex items-center flex-1 min-w-0 gap-4" data-id="element-202">
          <div className="relative flex-1 max-w-md" data-id="element-203">
            <input type="text" placeholder="Search products..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-204" />
            <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-205" />
          </div>
          <div className="flex items-center gap-3" data-id="element-206">
            <button className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-207">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-208" />
              Filter
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-209">
          {selectedProducts.size > 0 ? <div className="flex items-center gap-4" data-id="element-210">
              <span className="text-sm text-gray-600" data-id="element-211">
                {selectedProducts.size} products selected
              </span>
              <button onClick={handleClearSelection} className="text-sm text-gray-600 hover:text-gray-900" data-id="element-212">
                Clear selected products
              </button>
            </div> : (category === 'perfect-match' || category === 'potential-match') && <button onClick={() => {
          const allProductIds = new Set(filteredProducts.map(p => p.id));
          setSelectedProducts(allProductIds);
          setLinkedProducts(filteredProducts);
        }} className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-id="element-213">
                Link All
              </button>}
        </div>
      </div>
      <div className="mb-6" data-id="element-214">
        <AnimatePresence mode="wait" data-id="element-215">
          <motion.div key={activeCategory} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="p-4 bg-white rounded-lg border border-gray-200" data-id="element-216">
            {activeCategory === 'all' && <div className="flex items-start" data-id="element-217">
                <LayoutGridIcon className="h-5 w-5 text-blue-500 mt-0.5" data-id="element-218" />
                <div className="ml-3" data-id="element-219">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-220">
                    All Products
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-221">
                    Viewing all products across categories. Use the tabs above
                    to filter by match type.
                  </p>
                  {(linkedProducts.length > 0 || skippedProducts.length > 0) && <div className="mt-2 flex flex-wrap gap-2" data-id="element-222">
                      {linkedProducts.length > 0 && <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700" data-id="element-223">
                          {linkedProducts.length} to link
                        </span>}
                      {skippedProducts.length > 0 && <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700" data-id="element-224">
                          {skippedProducts.length} to skip
                        </span>}
                    </div>}
                </div>
              </div>}
            {activeCategory === 'perfect-match' && <div className="flex items-start" data-id="element-225">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" data-id="element-226" />
                <div className="ml-3" data-id="element-227">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-228">
                    Perfect Matches
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-229">
                    These products have exact matches on Amazon. Review and
                    confirm the matches.
                  </p>
                </div>
              </div>}
            {activeCategory === 'potential-match' && <div className="flex items-start" data-id="element-230">
                <AlertCircleIcon className="h-5 w-4 text-yellow-500 mt-0.5" data-id="element-231" />
                <div className="ml-3" data-id="element-232">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-233">
                    Potential Matches
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-234">
                    These products have possible matches on Amazon. Review each
                    match carefully.
                  </p>
                </div>
              </div>}
            {activeCategory === 'no-match' && <div className="flex items-start" data-id="element-235">
                <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5" data-id="element-236" />
                <div className="ml-3" data-id="element-237">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-238">
                    No Matches
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-239">
                    These products don't have matches on Amazon. You can create
                    new listings or skip them for now.
                  </p>
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
      <AnimatePresence mode="wait" data-id="element-240">
        <motion.div key={activeCategory} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="space-y-6" data-id="element-241">
          {filteredProducts.length > 0 ? filteredProducts.map(product => product.matchCategory === 'no-match' ? <ProductCard key={product.id} product={product} matchCategory={product.matchCategory} onSkip={() => handleProductSkip(product)} isSelectable={true} isSelected={selectedProducts.has(product.id)} onSelect={() => handleToggleSelect(product.id)} data-id="element-242" /> : <ComparisonCard key={product.id} product={product} onLink={() => handleProductLink(product)} onSkip={() => handleProductSkip(product)} showCheckbox={true} isSelected={selectedProducts.has(product.id)} onSelect={() => handleToggleSelect(product.id)} data-id="element-243" />) : <div className="text-center py-10 bg-white rounded-lg shadow" data-id="element-244">
              <p className="text-gray-500" data-id="element-245">No products found</p>
            </div>}
        </motion.div>
      </AnimatePresence>
      <AnimatePresence data-id="element-246">
        {showBulkActions && <BulkActionBar selectedCount={selectedProducts.size} onLink={handleBulkLink} onSkip={handleBulkSkip} onClear={handleClearSelection} data-id="element-247" />}
      </AnimatePresence>
      <FloatingActionBar linkedCount={totalChanges} onReviewClick={() => setShowReview(true)} showSkippedCount={skippedProducts.length > 0} skippedCount={skippedProducts.length} data-id="element-248" />
      <ReviewModal isOpen={showReview} onClose={handleClose} onConfirm={handleConfirmLinks} products={linkedProducts} skippedProducts={skippedProducts} isLoading={isLinking} remainingCount={getRemainingCounts()} isSuccess={showSuccess} data-id="element-249" />
    </div>;
};
export default ProductList;